import { useState } from "react";
import "../CSS/trainingInfo.scss";

interface Topic {
  title: string;
  body: string;
  references?: string[];
}

const TOPICS: Topic[] = [
  {
    title: "TSS — Training Stress Score",
    body: `TSS measures the overall stress of a workout. Formula: TSS = IF² × duration (hours) × 100.

An easy 1-hour run might score 40–50 TSS. A hard 2-hour race effort might score 180+ TSS. A week with 400 TSS is moderate; 600+ is a heavy week.

TSS is independent of sport — a 50-TSS ride and a 50-TSS run represent similar physiological stress.`,
    references: [
      "Coggan, A. (2003). Training and Racing with a Power Meter.",
      "TrainingPeaks: trainingpeaks.com/learn/articles/what-is-tss",
    ],
  },
  {
    title: "IF — Intensity Factor",
    body: `IF is the ratio of your workout intensity to your threshold intensity. IF 1.0 = exactly at threshold (FTP or LTHR).

Typical values:
• Z1 easy recovery: IF 0.55–0.68
• Z2 aerobic / long: IF 0.68–0.82
• Z3 tempo: IF 0.82–0.88
• Z4 threshold: IF 0.88–0.95
• Z5 VO2max / race pace: IF 0.95–1.05+

Running IF is ~14% higher than cycling at the same zone, because running has higher metabolic cost (TrainingPeaks: ~65 TSS/h at moderate run effort vs ~50 TSS/h cycling).`,
    references: [
      "TrainingPeaks: trainingpeaks.com/learn/articles/intensity-factor",
    ],
  },
  {
    title: "CTL, ATL & TSB — Fitness, Fatigue & Form",
    body: `These three metrics come from Banister's Impulse-Response model:

• CTL (Chronic Training Load) = fitness. Exponential moving average of daily TSS over ~42 days. Building CTL requires consistent training over weeks and months.

• ATL (Acute Training Load) = fatigue. EMA of daily TSS over ~7 days. Rises quickly with hard training, drops with rest.

• TSB (Training Stress Balance) = form = CTL − ATL. Positive TSB → fresh and ready to race. Negative TSB → fatigued. Most athletes race best with TSB between 0 and +25.

A typical periodization cycle: build CTL → accept negative TSB → taper → TSB rises → race.`,
    references: [
      "Banister, E.W. (1991). Modeling elite athletic performance.",
      "Coggan, A. & Allen, H. (2010). Training and Racing with a Power Meter, 2nd ed.",
    ],
  },
  {
    title: "Polariserad träning — 80/20-regeln",
    body: `Research on elite endurance athletes (runners, cyclists, rowers, XC skiers) consistently shows they spend roughly 80% of training time at low intensity (Z1-2) and 20% at high intensity (Z4-5), with very little at moderate/threshold (Z3).

This is called "polarized training". The key insight: Z3 threshold work is neither easy enough to recover from nor hard enough to drive the top-end adaptations of Z4-5. It sits in the "grey zone".

Benefits of the polarized model:
• The easy work builds aerobic base and mitochondrial density
• The hard work drives VO2max and lactate threshold
• Threshold work is saved for peaking close to races

The planner uses your CTL to calculate your optimal intensity ratio — less trained athletes do less high-intensity work since recovery capacity is lower.`,
    references: [
      "Seiler, S. & Tønnessen, E. (2009). Intervals, Thresholds, and Long Slow Distance. Sportscience 13, 32-53.",
      "Seiler, S. (2010). What is Best Practice for Training Intensity and Duration Distribution? Int J Sports Physiol Perform.",
      "Stöggl, T. & Sperlich, B. (2014). Polarized training has greater impact on key endurance variables. Front Physiol.",
    ],
  },
  {
    title: "Polarisering baserat på träningsnivå",
    body: `The planner calculates your intensity ratio (fraction of weekly TSS for hard sessions) from your current CTL and TSB:

CTL-based intensity allocation:
• CTL < 30  →  5% hard (very low fitness — mostly aerobic base)
• CTL 30–50 → 10% hard
• CTL 50–70 → 15% hard
• CTL 70+   → 20% hard (Seiler's elite 80/20)

TSB modifier (current form):
• TSB < −25 → intensity halved (too fatigued)
• TSB < −15 → intensity reduced 35%
• TSB > +10 → intensity increased 15% (fresh, can push)

This means a CTL-70 athlete with TSB −30 generates a plan with ~8% hard sessions instead of 20% — the body needs to recover first.`,
    references: [
      "Seiler, S. (2010). What is Best Practice for Training Intensity and Duration Distribution?",
    ],
  },
  {
    title: "Periodi­sering — uppbyggnad, topp & taper",
    body: `The planner divides the time to your race into three phases:

• Build (uppbyggnad): Progressive overload +8% TSS per week. Builds CTL and aerobic capacity.
• Peak: 1–2 weeks at maximum load (~135% of starting weekly TSS). Highest training stress before tapering.
• Taper: Load drops −25% then −40% from peak. TSB rises. Legs get fresh for race day.
• Race week: Minimal load — 2 short easy sessions, then the race.

Phase lengths scale with time to race: 6+ weeks gets a full build; closer races go straight to peak/taper.`,
    references: [
      "Issurin, V. (2010). New Horizons for the Methodology and Physiology of Training Periodization. Sports Med.",
      "Mujika, I. (2010). Intense Training: The Key to Optimal Performance Before and During the Taper. Scand J Med Sci Sports.",
    ],
  },
];

function TopicCard({ topic }: { topic: Topic }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`ti__card${open ? " ti__card--open" : ""}`}>
      <button className="ti__card-header" onClick={() => setOpen(v => !v)}>
        <span>{topic.title}</span>
        <span className="ti__card-icon">{open ? "−" : "+"}</span>
      </button>
      {open && (
        <div className="ti__card-body">
          <p className="ti__card-text">{topic.body}</p>
          {topic.references && (
            <div className="ti__card-refs">
              <div className="ti__card-refs-label">Källor</div>
              {topic.references.map((r, i) => <div key={i} className="ti__card-ref">{r}</div>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function TrainingInfo() {
  return (
    <div className="ti">
      <h2 className="ti__title">Träningskunskap</h2>
      <p className="ti__subtitle">Principer och forskning bakom planeringen.</p>
      <div className="ti__cards">
        {TOPICS.map((t, i) => <TopicCard key={i} topic={t} />)}
      </div>
    </div>
  );
}
